// modules
import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  ButtonIcon,
  CheckboxSquareCheckedIcon,
  CheckboxSquareUncheckedIcon,
  InputField,
  RupeeIcon,
  Text,
  TextArea,
  View,
} from '@upstox/upstox-ui';
import Localization from 'modules/Localization';
import { useStore } from 'modules/Stores';
import { observer } from 'mobx-react';
// styles
import * as css from './styles';
// constants
import { maxNoteLength } from './constants';

const CreatePriceAlertBody: FC = () => {
  const {
    priceAboveValue,
    priceBelowValue,
    priceAboveNotes,
    priceBelowNotes,
    priceAboveError,
    priceBelowError,
    setPriceAbove,
    setPriceBelow,
    setPriceAboveNotes,
    setPriceBelowNotes,
  } = useStore('PriceAlertsStore');
  const { getLoading } = useStore('LoadingStore');

  const isCreatingAlert = getLoading('CREATE_PRICE_ALERT');
  const isEditingAlert = getLoading('EDIT_PRICE_ALERT');
  const loading = isCreatingAlert || isEditingAlert;

  const [isAddPriceAboveNotes, setIsAddPriceAboveNotes] = useState(false);
  const [isAddPriceBelowNotes, setIsAddPriceBelowNotes] = useState(false);

  useEffect(() => {
    if (priceAboveNotes) {
      setIsAddPriceAboveNotes(true);
    }
    if (priceBelowNotes) {
      setIsAddPriceBelowNotes(true);
    }
  }, [priceAboveNotes, priceBelowNotes]);

  const handlePriceAboveChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPriceAbove(e.target.value);
    },
    [setPriceAbove],
  );

  const handlePriceBelowChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPriceBelow(e.target.value);
    },
    [setPriceBelow],
  );

  const handlePriceAboveNotesChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (e.target.value.length > maxNoteLength) return;
      setPriceAboveNotes(e.target.value);
    },
    [setPriceAboveNotes],
  );

  const handlePriceBelowNotesChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (e.target.value.length > maxNoteLength) return;
      setPriceBelowNotes(e.target.value);
    },
    [setPriceBelowNotes],
  );

  return (
    <View cssProps={{ loading }} css={css.container}>
      <InputField
        type="number"
        id="priceAbove"
        name="priceAbove"
        label={Localization.translate('PRICE_ABOVE')}
        value={priceAboveValue}
        error={priceAboveError}
        before={(
          <RupeeIcon
            css={css.rupeeIcon}
            color="text.2"
            size="small"
          />
        )}
        onChange={handlePriceAboveChange}
      />

      <View alignItems="center" gap={8}>
        <ButtonIcon
          onClick={() => setIsAddPriceAboveNotes(!isAddPriceAboveNotes)}
          variant="white"
          css={css.checkboxButton}
        >
          {isAddPriceAboveNotes ? (
            <CheckboxSquareCheckedIcon color="interactive.primary.default" />
          ) : (
            <CheckboxSquareUncheckedIcon color="text.2" />
          )}
        </ButtonIcon>
        <Text variant="meta">{Localization.translate('ADD_NOTES')}</Text>
        <Text css={css.lengthValidation}>
          {Localization.translate('STRING_LENGTH_VALIDATION', priceAboveNotes.length.toString(), maxNoteLength.toString())}
        </Text>
      </View>
      {isAddPriceAboveNotes && (
        <TextArea
          label={Localization.translate('ADD_NOTES')}
          value={priceAboveNotes}
          onChange={handlePriceAboveNotesChange}
          placeholder={Localization.translate('WRITE_NOTE_HERE')}
          rows={3}
          css={css.textArea}
        />
      )}

      <InputField
        type="number"
        label={Localization.translate('PRICE_BELOW')}
        id="priceBelow"
        name="priceBelow"
        value={priceBelowValue}
        error={priceBelowError}
        onChange={handlePriceBelowChange}
        before={(
          <RupeeIcon
            css={css.rupeeIcon}
            color="text.2"
            size="small"
          />
        )}
      />
      <View alignItems="center" gap={8}>
        <ButtonIcon
          onClick={() => setIsAddPriceBelowNotes(!isAddPriceBelowNotes)}
          variant="white"
          css={css.checkboxButton}
        >
          {isAddPriceBelowNotes ? (
            <CheckboxSquareCheckedIcon color="interactive.primary.default" />
          ) : (
            <CheckboxSquareUncheckedIcon color="text.2" />
          )}
        </ButtonIcon>
        <Text variant="meta">{Localization.translate('ADD_NOTES')}</Text>
        <Text css={css.lengthValidation}>
          {Localization.translate('STRING_LENGTH_VALIDATION', priceBelowNotes.length.toString(), maxNoteLength.toString())}
        </Text>
      </View>
      {isAddPriceBelowNotes && (
        <TextArea
          label={Localization.translate('ADD_NOTES')}
          value={priceBelowNotes}
          onChange={handlePriceBelowNotesChange}
          placeholder={Localization.translate('WRITE_NOTE_HERE')}
          rows={3}
          css={css.textArea}
        />
      )}
    </View>
  );
};

export default observer(CreatePriceAlertBody);
